import { array, boolean, nonEmptyArray, option, string } from "fp-ts";
import { NonEmptyArray } from "fp-ts/lib/NonEmptyArray";
import { constTrue, flip, flow, pipe } from "fp-ts/lib/function";
import removeAccents from "remove-accents";
import {
  Accessor,
  Filter,
  FilterDefinition,
  PathChoice,
} from "../parsers/moduleFilterParser";

export class ModuleFilter {
  static accessByDefinition<T extends {}>(
    filterDefinition: FilterDefinition
  ): (t: T) => any[] {
    const lookupFunctions = pipe(
      filterDefinition.propertyPaths,
      array.map(ModuleFilter.accessorToLookupFunction)
    );
    return (t) =>
      pipe(
        lookupFunctions,
        array.map((accessor) => accessor(t)),
        array.flatten
      );
  }

  private static take(t: any): any[] {
    if (pipe(t, option.fromNullable, option.isNone)) return [];
    if (Array.isArray(t)) return t;
    return [t];
  }

  private static takeChoice(choice: PathChoice): (t: any) => any[] {
    var choicePredicate: any = pipe(
      choice.pathFilter,
      option.map(ModuleFilter.queryAsPredicate),
      option.getOrElseW(() => constTrue)
    );
    choicePredicate["debug"] = choice.pathFilter;
    return (t: any) =>
      pipe(
        t[choice.pathKey],
        ModuleFilter.take,
        array.filter((value) => {
          const result = choicePredicate(value);
          return result;
        })
      );
  }

  static accessorToLookupFunction(accessor: Accessor): (t: any) => any[] {
    return pipe(
      accessor,
      nonEmptyArray.map(ModuleFilter.takeChoice),
      nonEmptyArray.reduceRight(ModuleFilter.take, (headLookup, tailLookup) => {
        return (t) => pipe(t, headLookup, array.flatMap(tailLookup));
      })
    );
  }

  static textSearch(needle: string): (haystack: string) => boolean {
    return (haystack) =>
      removeAccents(haystack.toLowerCase().normalize()).includes(
        removeAccents(needle.toLowerCase().normalize())
      );
  }

  static queryAsPredicate(filters: NonEmptyArray<Filter>): (x: any) => boolean {
    return pipe(
      filters,
      nonEmptyArray.map(ModuleFilter.asPredicate),
      nonEmptyArray.concatAll({ concat: (a, b) => (t) => a(t) && b(t) })
    );
  }

  static asPredicate(filter: Filter): (x: any) => boolean {
    const accessor = ModuleFilter.accessByDefinition(filter.definition);
    const type = filter.definition.type;
    const needles = array.uniq(string.Eq)(filter.values);
    const getValues = (t: any) =>
      pipe(
        accessor(t),
        array.map((x) => x.toString()),
        array.uniq(string.Eq)
      );

    // Hjælpefunktion: Konverter "now" eller Unix timestamp til numerisk timestamp
    const resolveNeedleTimestamp = (needle: string): number => {
      if (needle.toLowerCase() === "now") {
        return Math.floor(Date.now() / 1000); // Aktuel Unix timestamp (sekunder)
      }
      return parseInt(needle, 10); // Antag at needle er et Unix timestamp
    };

    // Hjælpefunktion: Tjek om en streng er "now" eller et gyldigt tal (Unix timestamp)
    const isValidTimestampOrNow = (value: string): boolean => {
      if (value.toLowerCase() === "now") return true;
      return !isNaN(parseInt(value, 10));
    };

    switch (type) {
      case "and":
        return flow(
          getValues,
          array.intersection(string.Eq)(needles),
          (commonValues) => commonValues.length == needles.length
        );
      case "or":
        return flow(
          getValues,
          array.intersection(string.Eq)(needles),
          (commonValues) => commonValues.length > 0
        );
      case "not":
        return flow(
          getValues,
          array.intersection(string.Eq)(needles),
          (commonValues) => commonValues.length == 0
        );
      case "search":
        return flow(getValues, (haystacks) => {
          const words = splitStringsBySpace(needles);
          const IsInAny = (haystacks: string[]) => (needle: string) =>
            array.foldMap(boolean.MonoidAny)(ModuleFilter.textSearch(needle))(
              haystacks
            );
          return array.foldMap(boolean.MonoidAll)(IsInAny(haystacks))(words);
        });
      case "before":
        if (!isValidTimestampOrNow(needles[0])) return () => false;
        const needleTimestampBefore = resolveNeedleTimestamp(needles[0]);
        return flow(
          getValues,
          array.every((value) => {
            if (!isValidTimestampOrNow(value)) return false;
            const valueTimestamp = parseInt(value, 10);
            return valueTimestamp < needleTimestampBefore;
          })
        );
      case "after":
        if (!isValidTimestampOrNow(needles[0])) return () => false;
        const needleTimestampAfter = resolveNeedleTimestamp(needles[0]);
        return flow(
          getValues,
          array.every((value) => {
            if (!isValidTimestampOrNow(value)) return false;
            const valueTimestamp = parseInt(value, 10);
            return valueTimestamp > needleTimestampAfter;
          })
        );
      case "expired":
        if (!isValidTimestampOrNow(needles[0])) return () => false;
        const needleTimestamp = resolveNeedleTimestamp(needles[0]);
        return flow(
          getValues,
          array.every((value) => {
            if (!isValidTimestampOrNow(value)) return true;
            const valueTimestamp = parseInt(value, 10);
            return valueTimestamp >= needleTimestamp;
          })
        );
      default:
        return constTrue;
    }
  }
}

function splitStringsBySpace(strings: Array<string>): Array<string> {
  return pipe(
    strings,
    array.flatMap((string: string) => string.split(" "))
  );
}
